import type { ComputedRef, Ref } from 'vue'
import type { LocationQueryRaw } from 'vue-router'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { NativeSelectOption } from '@shared/ui/fields'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'

import { useCustomersStore } from './store'
import { useCustomerOptions } from './customer-options'
import { useCustomersApi } from '../../api/list/list.hook'

/** The native select needs a real option for "no status filter"; legacy used the same value. */
const STATUS_ALL = 'ALL'

interface FiltersCustomersReturn {
  refSearch: Ref<string>
  refDateRange: Ref<Date[] | null>
  refStages: Ref<string[]>
  statusValue: ComputedRef<string>
  statusOptions: ComputedRef<NativeSelectOption[]>
  stageOptions: ComputedRef<AppSelectOption[]>
  setStatus: (option: NativeSelectOption) => void
  isLoading: ComputedRef<boolean>
  submitSearch: () => Promise<void>
  clearFilters: () => Promise<void>
}

/**
 * The controls hold a draft: like the legacy page, nothing is requested until "Search" (or Enter in the
 * search input), so typing never fires a full-text query per keystroke. The store keeps the applied
 * filters, which the middleware reads from the URL.
 */
export const useFiltersCustomers = (): FiltersCustomersReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useCustomersStore()
  const dayjs = useDayjs()
  const { statusOptions, stageOptions } = useCustomerOptions()

  const refSearch = ref<string>('')
  const refDateRange = ref<Date[] | null>(null)
  const refStages = ref<string[]>([])
  const refStatus = ref<string | undefined>(undefined)

  const isLoading = computed<boolean>(() => store.isLoading)

  const statusValue = computed<string>(() => refStatus.value ?? STATUS_ALL)
  const statusNativeOptions = computed<NativeSelectOption[]>(() => [
    { value: STATUS_ALL, text: 'All' },
    ...statusOptions.value,
  ].map(({ value, text }) => ({ id: value, value, text, selected: value === statusValue.value })))

  const setStatus = (option: NativeSelectOption): void => {
    refStatus.value = option.value === STATUS_ALL ? undefined : String(option.value)
  }

  const toDateRange = (from: string | null, to: string | null): Date[] | null => {
    if (!from || !to) return null

    const fromDate = dayjs(from)
    const toDate = dayjs(to)

    return fromDate.isValid() && toDate.isValid() ? [fromDate.toDate(), toDate.toDate()] : null
  }

  const syncDraft = (): void => {
    refSearch.value = store.getFilterSearch ?? ''
    refDateRange.value = toDateRange(store.getFilterFrom, store.getFilterTo)
    refStages.value = [...store.getFilterStages]
    refStatus.value = store.getFilterStatus ?? undefined
  }

  /** The API takes the range only as a pair, so a half-picked range is not sent. */
  const getDraftRange = (): { from?: string, to?: string } => {
    const [from, to] = refDateRange.value ?? []

    return from && to
      ? { from: dayjs(from).format(DateFormat.Default), to: dayjs(to).format(DateFormat.Default) }
      : {}
  }

  /** Re-running the current query refetches instead of being a no-op navigation, as the legacy button did. */
  const pushQuery = async (query: LocationQueryRaw): Promise<void> => {
    if (router.resolve({ query }).fullPath === route.fullPath) {
      await useCustomersApi()
      return
    }

    await router.push({ query })
  }

  const submitSearch = async (): Promise<void> => {
    const { from, to } = getDraftRange()

    await pushQuery({
      ...route.query,
      q: refSearch.value.trim() || undefined,
      from,
      to,
      stage: refStages.value.length ? refStages.value : undefined,
      status: refStatus.value || undefined,
      page: undefined,
    })
  }

  /** Drops every filter, the inbound-link ones without a control included; the page size stays. */
  const clearFilters = async (): Promise<void> => {
    await pushQuery({
      per_page: route.query.per_page,
    })
  }

  watch(() => route.query, syncDraft, { immediate: true })

  return {
    refSearch,
    refDateRange,
    refStages,
    statusValue,
    statusOptions: statusNativeOptions,
    stageOptions,
    setStatus,
    isLoading,
    submitSearch,
    clearFilters,
  }
}
